// Turns selected print items + quantities into an ordered, fully rendered set
// of barcode labels, or explains exactly which item prevents printing.

import { getPrintItemKey, PRINT_BLOCK_REASONS } from "./catalogBarcodePrintItems";
import { normalizeCopiesInputValue } from "./catalogBarcodePrintQuantity";
import {
  getDefaultDocument,
  BARCODE_DENSITY,
  classifyBarcodeDensity,
  estimateModuleWidthMM,
  renderBarcodeSet,
} from "./engine/barcodeRender";
import {
  DEFAULT_LABEL_CONTENT,
  getBarcodeLabelData,
} from "./engine/barcodeLabelTemplate";
import { resolveLabelTemplate } from "./engine/labelTemplates";
import { computeSheetMetrics } from "./engine/sheetLayout";
import { buildLabelSheetDocument, LABEL_SHEET_MODES } from "./engine/labelSheetDocument";
import { openPrintDocument } from "./engine/printDocument";

/**
 * @returns {{
 *   lines: Array<{ item, key, copies, label }>,
 *   labels: Array<{ key, copyIndex, label, barcodeSvg }>,
 *   totalLabels: number,
 *   failures: Array<{ item, key, reason, message }>,
 *   densityWarnings: Array<{ item, key, moduleWidthMM, level: "warning" | "unsafe" }>,
 *   density: "healthy" | "warning" | "unsafe",   // the worst line
 * }}
 */
export const buildBarcodeLabelJob = ({
  items,
  copiesByKey = {},
  geometry,
  content = DEFAULT_LABEL_CONTENT,
  templateId,
  doc = getDefaultDocument(),
}) => {
  const lines = (Array.isArray(items) ? items : [])
    .map((item) => {
      const key = getPrintItemKey(item);
      return {
        item,
        key,
        copies: normalizeCopiesInputValue(copiesByKey[key], 0),
        label: getBarcodeLabelData(item, content),
      };
    })
    .filter((line) => line.copies > 0);

  const rendered = renderBarcodeSet(
    lines.map((line) => line.label.barcode),
    {},
    doc
  );
  const drawableWidthMM = resolveLabelTemplate(templateId).getBarcodeDrawableWidthMM(
    computeSheetMetrics(geometry).labelWidthMM,
    geometry,
    content
  );

  const failures = [];
  const densityWarnings = [];
  const labels = [];

  lines.forEach((line) => {
    const result = rendered.get(line.label.barcode);
    if (!result?.ok) {
      failures.push({
        item: line.item,
        key: line.key,
        reason:
          result?.reason === "empty"
            ? PRINT_BLOCK_REASONS.MISSING_BARCODE
            : PRINT_BLOCK_REASONS.UNRENDERABLE_BARCODE,
        message: result?.message || "",
      });
      return;
    }

    const moduleWidthMM = estimateModuleWidthMM(result.moduleCount, drawableWidthMM);
    const level = classifyBarcodeDensity(moduleWidthMM);
    if (level !== BARCODE_DENSITY.HEALTHY) {
      densityWarnings.push({ item: line.item, key: line.key, moduleWidthMM, level });
    }

    // Copies of one item print next to each other.
    for (let copyIndex = 1; copyIndex <= line.copies; copyIndex += 1) {
      labels.push({ key: line.key, copyIndex, label: line.label, barcodeSvg: result.svg });
    }
  });

  return {
    lines,
    labels,
    totalLabels: lines.reduce((sum, line) => sum + line.copies, 0),
    failures,
    densityWarnings,
    density: densityWarnings.some((warning) => warning.level === BARCODE_DENSITY.UNSAFE)
      ? BARCODE_DENSITY.UNSAFE
      : densityWarnings.length
      ? BARCODE_DENSITY.WARNING
      : BARCODE_DENSITY.HEALTHY,
  };
};

export class BarcodeLabelJobBlockedError extends Error {
  constructor(failures) {
    super("Some labels cannot be printed.");
    this.name = "BarcodeLabelJobBlockedError";
    this.failures = failures;
  }
}

/** Opens the print dialog for a job. Never prints a partial job. */
export const printBarcodeLabelJob = ({ job, geometry, calibration, startPosition, templateId }) => {
  if (job.failures.length) throw new BarcodeLabelJobBlockedError(job.failures);
  if (!job.labels.length) throw new Error("No barcode labels available to print.");

  const { html, plan } = buildLabelSheetDocument({
    labels: job.labels,
    geometry,
    calibration,
    startPosition,
    templateId,
    mode: LABEL_SHEET_MODES.PRINT,
  });
  openPrintDocument(html);
  return plan;
};
